import { type Image, type GeneratedImage } from '@prisma/client'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { type Dispatch, type SetStateAction } from 'react'
import { decodeImageFormat } from '#app/routes/generate+/_layout'
import { type GeneratedImage2 } from '#app/routes/resources+/generated-images'
import {
	type DBModel,
	getDisplayModel,
	type DBSampler,
	getDisplaySampler,
	type DBScheduler,
	getDisplayScheduler,
} from '#app/utils/helpers'
import { useGeneratedImageData } from '#app/utils/hooks/useGeneratedImageData'
import { useImageUrl } from '#app/utils/hooks/useImageUrl'
import { useMediaQuery } from '#app/utils/hooks/useMediaQuery'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user.js'
import { CustomImage } from './custom-image'
import { NsfwImageOverlay } from './nsfw-image-overlay'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogTitle } from './ui/dialog'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from './ui/drawer'
import { Icon } from './ui/icon'
import { ScrollArea } from './ui/scroll-area'
import { Separator } from './ui/separator'
import { Skeleton } from './ui/skeleton'

import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './ui/tooltip'
// Separate PropertyItem component
function PropertyItem({
	title,
	value,
	icon,
	className = '',
}: PropertyItemProps) {
	const copyToClipboard = async () => {
		if (typeof value === 'string' || typeof value === 'number') {
			try {
				await navigator.clipboard.writeText(value.toString())
			} catch (error) {
				console.error('Failed to copy to clipboard:', error)
			}
		}
	}

	const showCopyButton =
		icon === 'lightbulb-filament' || icon === 'prohibit' || icon === 'plant'

	return (
		<div
			className={cn(
				'group relative overflow-hidden rounded-lg bg-accent p-4 transition-all',
				className,
			)}
		>
			<div className="mb-2 flex items-center space-x-2">
				<Icon name={icon} className="text-lg text-primary" />
				<h4 className="text-sm font-medium text-gray-600">{title}</h4>
			</div>
			<p className="text-base font-semibold text-accent-foreground">{value}</p>
			{showCopyButton && (
				<div className="absolute right-2 top-2">
					<TooltipProvider delayDuration={200}>
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									onClick={copyToClipboard}
									variant="ghost"
									size="icon"
									className="opacity-0 transition-opacity group-hover:opacity-100"
									aria-label="Copy to clipboard"
								>
									<Icon name="copy" className="h-4 w-4" />
								</Button>
							</TooltipTrigger>
							<TooltipContent>
								<p>Copy to clipboard</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>
			)}
		</div>
	)
}

// Separate ImageSpecs component
function ImageSpecs({
	isLoading,
	imageData,
}: {
	isLoading: boolean
	imageData: (GeneratedImage & { image: Image }) | undefined
}) {
	const imageFormat = imageData?.image
		? decodeImageFormat(imageData.image.format)
		: null

	const renderedImageHeight =
		document.getElementById('image-container')?.offsetHeight
	const isDesktop = useMediaQuery('(min-width: 768px)')

	return (
		<div className="h-full w-full overflow-auto bg-gradient-to-tr from-background to-muted">
			<div className="h-full p-6 md:p-8">
				<h2 className="mb-6 text-3xl font-bold text-foreground md:text-4xl">
					Image Specs
				</h2>
				<Separator className="mb-8 h-px bg-gradient-to-r from-primary via-secondary to-primary" />
				{renderedImageHeight && (
					<ScrollArea
						className="px-4"
						viewportStyle={{
							...(isDesktop && { maxHeight: `${renderedImageHeight - 100}px` }),
						}}
					>
						{isLoading ? (
							<div className="grid grid-cols-1 gap-6">
								{[...Array(6)].map((_, index) => (
									<Skeleton
										key={index}
										className="h-24 w-full animate-pulse rounded-xl bg-gray-200"
									/>
								))}
							</div>
						) : (
							<div className="grid grid-cols-1 gap-6 md:grid-cols-2">
								<PropertyItem
									title="Prompt"
									value={imageData?.positivePrompt}
									icon="lightbulb-filament"
									className="col-span-2"
								/>
								{imageData?.negativePrompt && (
									<PropertyItem
										title="Negative Prompt"
										value={imageData?.negativePrompt}
										icon="prohibit"
										className="col-span-2"
									/>
								)}
								<PropertyItem
									title="Model"
									value={getDisplayModel(imageData?.model as DBModel)}
									icon="palette"
									className="col-span-2 lg:col-span-1"
								/>
								<PropertyItem
									title={`${imageData?.model.includes('flux') ? 'Aspect Ratio' : 'Resolution'}`}
									value={
										imageData?.model.includes('flux')
											? `${imageFormat?.ratio.width} : ${imageFormat?.ratio.height}`
											: imageFormat
												? `${imageFormat.resolution.width}px × ${imageFormat.resolution.height}px (${imageFormat.ratio.width}︰${imageFormat.ratio.height})`
												: ''
									}
									icon="vector-two"
									className="col-span-2 lg:col-span-1"
								/>
								{!imageData?.model.includes('flux') && (
									<>
										<PropertyItem
											title="Sampler"
											value={getDisplaySampler(imageData?.sampler as DBSampler)}
											icon="bezier-curve"
											className="col-span-2 lg:col-span-1"
										/>
										<PropertyItem
											title="Scheduler"
											value={getDisplayScheduler(
												imageData?.scheduler as DBScheduler,
											)}
											icon="compass-tool"
											className="col-span-2 lg:col-span-1"
										/>
									</>
								)}
								<PropertyItem
									title="Steps"
									value={imageData?.steps}
									icon="steps"
									className="col-span-2 lg:col-span-1"
								/>
								<PropertyItem
									title="Seed"
									value={String(imageData?.seed)}
									icon="plant"
									className="col-span-2 lg:col-span-1"
								/>
								{!imageData?.model.includes('flux1.1') && (
									<PropertyItem
										title={`${imageData?.model === 'flux1-dev' ? 'Guidance' : 'CFG'}`}
										value={String(imageData?.cfg)}
										icon="sticker"
										className="col-span-2 lg:col-span-1"
									/>
								)}
								<PropertyItem
									title="Created"
									value={new Date(
										imageData?.image.createdAt || Date.now(),
									).toLocaleString('en-US', {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
										hour12: true,
									})}
									icon="clock"
									className="col-span-2 lg:col-span-1"
								/>
							</div>
						)}
					</ScrollArea>
				)}
			</div>
		</div>
	)
}

export function DialogueImage({ open, onClose, image }: DialogueImageProps) {
	const imageFormat = decodeImageFormat(image.format)

	const isDesktop = useMediaQuery('(min-width: 768px)')
	const { actualImageUrl, isLoading } = useImageUrl(image)
	const { data: generatedImageData, isLoading: isImageDataLoading } =
		useGeneratedImageData(open, image.id, image.ownerId)

	const user = useOptionalUser()

	const content = (
		<div className="flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 md:flex-row">
			<div className="flex h-full w-full items-center justify-center p-2 sm:p-4 md:w-1/2 md:p-6">
				<div
					id="image-container"
					className="relative w-full overflow-hidden rounded-lg border border-gray-400 border-opacity-20 shadow-lg sm:shadow-xl md:max-w-full md:shadow-2xl"
				>
					<div
						className="flex h-full items-center justify-center"
						style={{
							aspectRatio: `${imageFormat.ratio.width} / ${imageFormat.ratio.height}`,
						}}
					>
						<CustomImage
							id={image.id}
							image={image}
							width={imageFormat.resolution.width}
							actualImageUrl={actualImageUrl}
							imageFormat={imageFormat}
							className={cn(
								'h-auto max-h-[70vh] w-full object-contain md:max-h-full',
							)}
							style={{
								aspectRatio: `${imageFormat.ratio.width} / ${imageFormat.ratio.height}`,
							}}
						/>
						{image.nsfw && !user?.enableNsfw ? <NsfwImageOverlay /> : <></>}
					</div>
				</div>
			</div>
			<div className="w-full bg-white bg-opacity-50 backdrop-blur-sm md:w-1/2">
				<ImageSpecs
					isLoading={isLoading || isImageDataLoading}
					imageData={generatedImageData}
				/>
			</div>
		</div>
	)
	if (typeof isDesktop === 'undefined') {
		return <></>
	}

	if (isDesktop) {
		return (
			<Dialog open={open} onOpenChange={() => onClose(false)}>
				<DialogContent className="w-[95vw] max-w-6xl rounded-2xl bg-transparent p-0 shadow-2xl">
					<VisuallyHidden.Root>
						<DialogTitle>Image viewer</DialogTitle>
					</VisuallyHidden.Root>
					{content}
				</DialogContent>
			</Dialog>
		)
	}

	// TODO: fix drawer not closing when clicking outside
	return (
		<Drawer open={open} onClose={() => onClose(false)}>
			<DrawerContent className="rounded-t-2xl bg-gradient-to-b from-gray-50 to-gray-100">
				<DrawerHeader className="text-left">
					<DrawerTitle className="text-2xl font-bold text-primary">
						Image Viewer
					</DrawerTitle>
				</DrawerHeader>
				<ScrollArea className="h-[80vh] flex-grow">{content}</ScrollArea>
			</DrawerContent>
		</Drawer>
	)
}

// Add these type definitions at the end of the file
type PropertyItemProps = {
	title: string
	value?: string | number
	icon: any
	className?: string
}

type DialogueImageProps = {
	open: boolean
	onClose: Dispatch<SetStateAction<boolean>>
	image: GeneratedImage2
}
